import ActionDialog from "@dashboard/components/ActionDialog";
import { DashboardCard } from "@dashboard/components/Card";
import ResponsiveTable from "@dashboard/components/ResponsiveTable";
import TablePagination from "@dashboard/components/TablePagination";
import TableRowLink from "@dashboard/components/TableRowLink";
import { WsmErrorFragment } from "@dashboard/graphql";
import { buttonMessages } from "@dashboard/intl";
import { renderCollection } from "@dashboard/misc";
import { TableBody, TableCell, TableFooter, TableHead } from "@material-ui/core";
import { Box, Button, Input, Skeleton, Text, Textarea, TrashBinIcon } from "@saleor/macaw-ui-next";
import React, { useState } from "react";
import { MessageDescriptor, useIntl } from "react-intl";

import { getWsmFitmentErrorMessage } from "../errors";
import { useWsmFitmentAttempt } from "../hooks/useWsmFitmentAttempt";
import { WsmFitmentPagingProps } from "../hooks/useWsmFitmentPagedValues";
import { valueMessages } from "../messages";
import { WsmFitmentValueRow, WsmFitmentWriteResult } from "../types";
import { parseValueLines, parseValueLinesWithNumbers, placeBulkErrors } from "../utils";

const numberOfColumns = 2;

/**
 * The three writes the card asks for. Each resolves to how that one call came
 * back; the card shows those errors and nothing older, so a dialog reopened or
 * a different row edited starts clean.
 */
export interface WsmFitmentValueActions {
  onAdd: (values: string[]) => Promise<WsmFitmentWriteResult>;
  onRename: (id: string, value: string) => Promise<WsmFitmentWriteResult>;
  onDelete: (id: string) => Promise<WsmFitmentWriteResult>;
}

/** A server-side filter on the list; left out where the query has none. */
export interface WsmFitmentValuesSearch {
  value: string;
  onChange: (value: string) => void;
}

export interface WsmFitmentValuesCardProps extends WsmFitmentValueActions {
  title: string;
  values: WsmFitmentValueRow[] | undefined;
  totalCount?: number | null;
  paging: WsmFitmentPagingProps;
  disabled: boolean;
  /** The delete dialog's sentence, `{value}` filled in: what refuses the delete differs. */
  deleteMessage: MessageDescriptor;
  search?: WsmFitmentValuesSearch;
}

/**
 * The values of one axis or qualifier: paged, added in bulk from one-per-line
 * text, renamed in place, deleted one at a time with IN_USE shown on the dialog.
 *
 * Rows are shown in the order the API pages them, (updatedAt, id), not sorted
 * here: a client sort of one 50-row page would read as the whole list's order
 * and be wrong across pages. So a Year axis is not in numeric order, and a
 * renamed value moves to the last page on the next refetch; the search box
 * (server-side, owned by the view, so a rename does not clear it) is how a
 * merchant finds a value. The pickers do sort, per loaded set.
 */
export const WsmFitmentValuesCard = ({
  title,
  values,
  totalCount,
  paging,
  disabled,
  deleteMessage,
  search,
  onAdd,
  onRename,
  onDelete,
}: WsmFitmentValuesCardProps) => {
  const intl = useIntl();
  const [addOpen, setAddOpen] = useState(false);
  const [addText, setAddText] = useState("");
  const [editing, setEditing] = useState<WsmFitmentValueRow | null>(null);
  const [toDelete, setToDelete] = useState<WsmFitmentValueRow | null>(null);
  // Each set from the last call's result and cleared when its dialog opens or
  // the edited row changes: the mutation's own data would outlive both.
  // Every error of the last add, each with the value line it names.
  const [addErrors, setAddErrors] = useState<ReturnType<typeof placeBulkErrors>>([]);
  const [renameError, setRenameError] = useState<WsmErrorFragment | undefined>();
  const [deleteError, setDeleteError] = useState<WsmErrorFragment | undefined>();
  // Confirm buttons follow this card's own attempt, reset on open (see the hook).
  const addAttempt = useWsmFitmentAttempt();
  const deleteAttempt = useWsmFitmentAttempt();

  const openAdd = () => {
    // A fresh attempt every time: text typed before a cancel or a refusal
    // does not come back.
    setAddText("");
    setAddErrors([]);
    addAttempt.reset();
    setAddOpen(true);
  };
  const startEditing = (row: WsmFitmentValueRow | null) => {
    setRenameError(undefined);
    setEditing(row);
  };
  const openDelete = (row: WsmFitmentValueRow) => {
    setDeleteError(undefined);
    deleteAttempt.reset();
    setToDelete(row);
  };
  const submitAdd = async () => {
    const sent = parseValueLinesWithNumbers(addText);
    const result = await addAttempt.run(() => onAdd(sent.map(entry => entry.value)));

    setAddErrors(placeBulkErrors(result.errors, sent));

    if (result.saved) {
      setAddText("");
      setAddOpen(false);
    }
  };
  const submitRename = async () => {
    if (!editing) {
      return;
    }

    const result = await onRename(editing.id, editing.value);

    setRenameError(result.errors[0]);

    if (result.saved) {
      setEditing(null);
    }
  };

  return (
    <>
      <DashboardCard data-test-id="wsm-fitment-values">
        <DashboardCard.Header>
          <DashboardCard.Title>
            {title}
            {typeof totalCount === "number" && (
              <Text size={3} color="default2" marginLeft={2}>
                ({totalCount})
              </Text>
            )}
          </DashboardCard.Title>
          <DashboardCard.Toolbar>
            <Button
              variant="secondary"
              disabled={disabled}
              onClick={openAdd}
              data-test-id="wsm-fitment-values-add"
            >
              {intl.formatMessage(valueMessages.add)}
            </Button>
          </DashboardCard.Toolbar>
        </DashboardCard.Header>
        <DashboardCard.Content paddingX={0}>
          {search && (
            <Box paddingX={6} paddingBottom={4}>
              <Input
                size="small"
                data-test-id="wsm-fitment-values-search"
                label={intl.formatMessage(valueMessages.search)}
                value={search.value}
                onChange={event => search.onChange(event.target.value)}
              />
            </Box>
          )}
          <ResponsiveTable>
            <TableHead>
              <TableRowLink>
                <TableCell>{intl.formatMessage(valueMessages.colValue)}</TableCell>
                <TableCell />
              </TableRowLink>
            </TableHead>
            <TableBody>
              {renderCollection(
                values,
                row => (
                  <TableRowLink
                    key={row ? row.id : "skeleton"}
                    data-test-id="wsm-fitment-value-row"
                  >
                    <TableCell>
                      {!row && <Skeleton />}
                      {row && editing?.id === row.id && (
                        <Box display="flex" alignItems="center" gap={2}>
                          <Input
                            size="small"
                            data-test-id="wsm-fitment-value-rename-input"
                            value={editing.value}
                            disabled={disabled}
                            onChange={event =>
                              setEditing({ id: row.id, value: event.target.value })
                            }
                            onKeyDown={event => {
                              if (event.key === "Enter") {
                                submitRename();
                              }

                              if (event.key === "Escape") {
                                startEditing(null);
                              }
                            }}
                            error={!!renameError}
                            helperText={getWsmFitmentErrorMessage(renameError, intl)}
                          />
                          <Button
                            size="small"
                            disabled={disabled}
                            onClick={submitRename}
                            data-test-id="wsm-fitment-value-rename-save"
                          >
                            {intl.formatMessage(valueMessages.rename)}
                          </Button>
                          <Button
                            size="small"
                            variant="tertiary"
                            onClick={() => startEditing(null)}
                          >
                            {intl.formatMessage(buttonMessages.cancel)}
                          </Button>
                        </Box>
                      )}
                      {row && editing?.id !== row.id && (
                        <Text size={3} data-test-id="wsm-fitment-value-text">
                          {row.value}
                        </Text>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {row && editing?.id !== row.id && (
                        <Box display="flex" justifyContent="flex-end" gap={2}>
                          <Button
                            size="small"
                            variant="tertiary"
                            disabled={disabled}
                            onClick={() => startEditing({ id: row.id, value: row.value })}
                            data-test-id="wsm-fitment-value-rename"
                          >
                            {intl.formatMessage(valueMessages.rename)}
                          </Button>
                          <Button
                            size="small"
                            variant="tertiary"
                            icon={<TrashBinIcon />}
                            title={intl.formatMessage(valueMessages.delete)}
                            disabled={disabled}
                            onClick={() => openDelete(row)}
                            data-test-id="wsm-fitment-value-delete"
                          />
                        </Box>
                      )}
                    </TableCell>
                  </TableRowLink>
                ),
                () => (
                  <TableRowLink data-test-id="wsm-fitment-values-empty">
                    <TableCell colSpan={numberOfColumns}>
                      <Text size={3} color="default2">
                        {intl.formatMessage(
                          search?.value.trim() ? valueMessages.noMatches : valueMessages.empty,
                        )}
                      </Text>
                    </TableCell>
                  </TableRowLink>
                ),
              )}
            </TableBody>
            <TableFooter>
              <TableRowLink>
                <TablePagination
                  colSpan={numberOfColumns}
                  settings={{ rowNumber: paging.rowNumber }}
                  onUpdateListSettings={(_, value) => paging.onRowNumberChange(value as number)}
                  hasNextPage={paging.hasNextPage}
                  hasPreviousPage={paging.hasPreviousPage}
                  onNextPage={paging.onNextPage}
                  onPreviousPage={paging.onPreviousPage}
                />
              </TableRowLink>
            </TableFooter>
          </ResponsiveTable>
        </DashboardCard.Content>
      </DashboardCard>
      <ActionDialog
        open={addOpen}
        title={intl.formatMessage(valueMessages.add)}
        confirmButtonState={addAttempt.state}
        disabled={disabled || parseValueLines(addText).length === 0}
        onClose={() => setAddOpen(false)}
        onConfirm={submitAdd}
      >
        <Textarea
          rows={8}
          data-test-id="wsm-fitment-values-add-text"
          label={intl.formatMessage(valueMessages.colValue)}
          value={addText}
          disabled={disabled}
          onChange={event => setAddText(event.target.value)}
          error={addErrors.length > 0}
          helperText={intl.formatMessage(valueMessages.addHelp)}
        />
        {addErrors.map(({ error, row }, index) => {
          const message = getWsmFitmentErrorMessage(error, intl);

          return (
            <Text
              key={index}
              size={2}
              color="critical1"
              display="block"
              marginTop={2}
              data-test-id="wsm-fitment-values-add-error"
            >
              {row
                ? intl.formatMessage(valueMessages.addLineError, {
                    line: row.line,
                    value: row.value,
                    message,
                  })
                : message}
            </Text>
          );
        })}
      </ActionDialog>
      <ActionDialog
        open={!!toDelete}
        variant="delete"
        title={intl.formatMessage(valueMessages.deleteTitle)}
        confirmButtonState={deleteAttempt.state}
        onClose={() => setToDelete(null)}
        onConfirm={async () => {
          if (!toDelete) {
            return;
          }

          const result = await deleteAttempt.run(() => onDelete(toDelete.id));

          setDeleteError(result.errors[0]);

          if (result.saved) {
            setToDelete(null);
          }
        }}
      >
        <Text display="block">
          {intl.formatMessage(deleteMessage, { value: toDelete?.value ?? "" })}
        </Text>
        {deleteError && (
          <Text
            display="block"
            color="critical1"
            marginTop={2}
            data-test-id="wsm-fitment-value-delete-error"
          >
            {getWsmFitmentErrorMessage(deleteError, intl)}
          </Text>
        )}
      </ActionDialog>
    </>
  );
};

export default WsmFitmentValuesCard;
